import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  roles?: string[];
  phone?: string;
  avatarUrl?: string;
  isVendor?: boolean;
  vendorStatus?: string;
  driverApplicationStatus?: string;
  isDriver?: boolean;
  isAssignedDriver?: boolean;
  courierCompanyId?: string;
  courierCompanyName?: string;
  isAdmin?: boolean;
  isCourier?: boolean;
}

// Helper function to check if user has a specific role
export const hasRole = (user: User | null, role: string): boolean => {
  if (!user) return false;
  return (user.roles || [user.role]).includes(role);
};

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  phoneLogin: (phone: string) => Promise<void>;
  phoneRegister: (phone: string, fullName?: string) => Promise<void>;
  verifyPhone: (phone: string, token: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as loading to check auth
  const [isCheckingAuth, setIsCheckingAuth] = useState(false); // Prevent multiple simultaneous checks

  // Check if user is already authenticated on app startup
  const checkAuth = async () => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuth) {
      console.log('checkAuth: Already checking auth, skipping...');
      return;
    }

    try {
      setIsCheckingAuth(true);
      console.log('checkAuth: Starting auth check...');

      // Get current session from Supabase
      const { data: { session }, error } = await supabase.auth.getSession();

      console.log('checkAuth: Session result:', { session: !!session, error });

      if (error) {
        console.error('Session error:', error);
        setIsLoading(false);
        return;
      }

      if (!session) {
        console.log('checkAuth: No session found, setting loading to false');
        setIsLoading(false);
        return;
      }

      console.log('checkAuth: Session found, fetching profile...');

      console.log('checkAuth: Fetching user profile from backend...');

      // Fetch user profile from our backend API
      const profileResponse = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        console.log('checkAuth: Profile data received:', profileData);

        // Check if user is a vendor
        let vendorStatus = null;
        try {
          const vendorResponse = await fetch(`${API_ENDPOINTS.STALLS.VENDORS}/profile`, {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          });

          if (vendorResponse.ok) {
            const vendorData = await vendorResponse.json();
            vendorStatus = vendorData.vendor?.status || null;
          }
        } catch (vendorError) {
          // User is not a vendor, which is fine
          console.log('checkAuth: User is not a vendor');
        }

        // Check if user has a driver application
        let driverApplicationStatus = null;
        try {
          const driverResponse = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/my-application`, {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          });

          if (driverResponse.ok) {
            const driverData = await driverResponse.json();
            driverApplicationStatus = driverData.application?.status || null;
          }
        } catch (driverError) {
          // User has no driver application, which is fine
          console.log('checkAuth: User has no driver application');
        }

        // Check if driver is assigned to a courier company
        let courierAssignment: any = null;
        if (driverApplicationStatus === 'approved') {
          try {
            const courierResponse = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/driver-assignment`, {
              headers: {
                'Authorization': `Bearer ${session.access_token}`
              }
            });

            if (courierResponse.ok) {
              const courierData = await courierResponse.json();
              courierAssignment = courierData.assignment || null;
            } else if (courierResponse.status === 404) {
              // Driver is not assigned to any company, which is fine
              console.log('checkAuth: Driver is not assigned to any courier company');
            }
          } catch (courierError) {
            // Driver is not assigned to any company, which is fine
            console.log('checkAuth: Driver is not assigned to any courier company');
          }
        }

        const userRoles = profileData.profile.roles || [profileData.profile.role || 'user'];

        const userData = {
          id: profileData.profile.id,
          email: profileData.profile.email || '',
          name: profileData.profile.fullName || profileData.profile.email || '',
          role: profileData.profile.role,
          roles: userRoles,
          isVendor: vendorStatus !== null,
          vendorStatus: vendorStatus || undefined,
          driverApplicationStatus: driverApplicationStatus || undefined,
          isDriver: driverApplicationStatus === 'approved',
          isAssignedDriver: courierAssignment !== null,
          courierCompanyId: courierAssignment?.courier_company_id || undefined,
          courierCompanyName: courierAssignment?.courier_company_name || undefined,
          isAdmin: userRoles.includes('admin'),
          isCourier: userRoles.includes('courier')
        };

        setUser(userData);

        // Store the access token for API calls
        localStorage.setItem('token', session.access_token);
        console.log('checkAuth: User set successfully');

        // Handle role-based routing on page load
        setTimeout(() => {
          const currentPath = window.location.pathname;

          // If user is on login/signup pages and is already authenticated, redirect to appropriate dashboard
          if (currentPath === '/auth/login' || currentPath === '/auth/signup' || currentPath === '/') {
            if (userData.isAdmin) {
              console.log('checkAuth: Redirecting authenticated admin to dashboard...');
              window.location.href = '/admin';
            } else if (userData.isCourier) {
              console.log('checkAuth: Redirecting authenticated courier to dashboard...');
              window.location.href = '/courier/dashboard';
            } else if (userData.isDriver) {
              console.log('checkAuth: Redirecting authenticated driver to dashboard...');
              window.location.href = '/driver/dashboard';
            }
            // Regular users stay on home page
          }
        }, 500); // Small delay to ensure state is set

      } else {
        console.error('checkAuth: Failed to fetch profile, status:', profileResponse.status);
        setIsLoading(false);
        setIsCheckingAuth(false);
        return;
      }

      setIsLoading(false);
      setIsCheckingAuth(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsLoading(false);
      setIsCheckingAuth(false);
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);

      if (event === 'SIGNED_OUT') {
        console.log('SIGNED_OUT event detected, clearing user state...');
        // User signed out, clear the user state
        setUser(null);
        localStorage.removeItem('token');
        setIsLoading(false);
        setIsCheckingAuth(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log('TOKEN_REFRESHED event detected, updating token...');
        // Token was refreshed, update the stored token
        localStorage.setItem('token', session.access_token);
      }
      // Note: We don't handle SIGNED_IN here to avoid infinite loops
      // The initial checkAuth() call will handle the session
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      console.log('Starting login process...');

      // Use Supabase Auth for login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Supabase login error:', error);
        throw new Error(error.message || 'Login failed');
      }

      if (!data.session) {
        console.error('No session created after login');
        throw new Error('No session created');
      }

      console.log('Supabase login successful, fetching user profile...');

      // Fetch user profile from our backend API
      const profileResponse = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${data.session.access_token}`
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        console.log('Login: Profile data received:', profileData);

        // Check if user is a vendor
        let vendorStatus = null;
        try {
          const vendorResponse = await fetch(`${API_ENDPOINTS.STALLS.VENDORS}/profile`, {
            headers: {
              'Authorization': `Bearer ${data.session.access_token}`
            }
          });

          if (vendorResponse.ok) {
            const vendorData = await vendorResponse.json();
            vendorStatus = vendorData.vendor?.status || null;
          }
        } catch (vendorError) {
          // User is not a vendor, which is fine
          console.log('Login: User is not a vendor');
        }

        // Check if user has a driver application
        let driverApplicationStatus = null;
        try {
          const driverResponse = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/my-application`, {
            headers: {
              'Authorization': `Bearer ${data.session.access_token}`
            }
          });

          if (driverResponse.ok) {
            const driverData = await driverResponse.json();
            driverApplicationStatus = driverData.application?.status || null;
          }
        } catch (driverError) {
          // User has no driver application, which is fine
          console.log('Login: User has no driver application');
        }

        // Check if driver is assigned to a courier company
        let courierAssignment: any = null;
        if (driverApplicationStatus === 'approved') {
          try {
            const courierResponse = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/driver-assignment`, {
              headers: {
                'Authorization': `Bearer ${data.session.access_token}`
              }
            });

            if (courierResponse.ok) {
              const courierData = await courierResponse.json();
              courierAssignment = courierData.assignment || null;
            } else if (courierResponse.status === 404) {
              // Driver is not assigned to any company, which is fine
              console.log('Login: Driver is not assigned to any courier company');
            }
          } catch (courierError) {
            // Driver is not assigned to any company, which is fine
            console.log('Driver is not assigned to any courier company');
          }
        }

        const userRoles = profileData.profile.roles || [profileData.profile.role || 'user'];

        const userData = {
          id: profileData.profile.id,
          email: profileData.profile.email || '',
          name: profileData.profile.fullName || profileData.profile.email || '',
          role: profileData.profile.role,
          roles: userRoles,
          isVendor: vendorStatus !== null,
          vendorStatus: vendorStatus || undefined,
          driverApplicationStatus: driverApplicationStatus || undefined,
          isDriver: driverApplicationStatus === 'approved',
          isAssignedDriver: courierAssignment !== null,
          courierCompanyId: courierAssignment?.courier_company_id || undefined,
          courierCompanyName: courierAssignment?.courier_company_name || undefined,
          isAdmin: userRoles.includes('admin'),
          isCourier: userRoles.includes('courier')
        };

        setUser(userData);

        // Store the access token for API calls
        localStorage.setItem('token', data.session.access_token);

        console.log('Login completed successfully');

        // Redirect based on user role after successful login
        setTimeout(() => {
          if (userData.isAdmin) {
            console.log('Redirecting admin to dashboard...');
            window.location.href = '/admin';
          } else if (userData.isCourier) {
            console.log('Redirecting courier to dashboard...');
            window.location.href = '/courier/dashboard';
          } else if (userData.isDriver) {
            console.log('Redirecting driver to dashboard...');
            window.location.href = '/driver/dashboard';
          } else {
            console.log('Redirecting regular user to home...');
            window.location.href = '/';
          }
        }, 1000); // Small delay to show success state

      } else {
        console.error('Login: Failed to fetch profile, status:', profileResponse.status);
        throw new Error('Failed to fetch user profile after login');
      }

    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const phoneLogin = async (phone: string) => {
    setIsLoading(true);
    try {
      console.log('Starting phone login process...');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/phone-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Phone login failed');
      }

      const data = await response.json();
      console.log('Phone login initiated:', data);

    } catch (error) {
      console.error('Phone login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const phoneRegister = async (phone: string, fullName?: string) => {
    setIsLoading(true);
    try {
      console.log('Starting phone registration process...');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/phone-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone, fullName })
      });

      if (!response.ok) {
        let errorMessage = 'Phone registration failed';

        try {
          // First, let's see what the raw response looks like
          const responseText = await response.text();
          console.log('Raw server response:', responseText);
          console.log('Response status:', response.status);
          console.log('Response headers:', Object.fromEntries(response.headers.entries()));

          const errorData = JSON.parse(responseText);
          console.log('Parsed server error response:', errorData);

          // Handle specific server error messages
          if (response.status === 400) {
            if (errorData.message?.includes('Phone already registered')) {
              errorMessage = 'This phone number is already registered. Please use phone login instead.';
            } else if (errorData.message?.includes('Validation failed')) {
              errorMessage = 'Please check your phone number format and try again.';
            } else if (errorData.message?.includes('Database error')) {
              errorMessage = 'Database error occurred. Please try again.';
            } else {
              errorMessage = errorData.message || 'Phone registration failed';
            }
          } else if (response.status === 500) {
            errorMessage = 'Server error occurred. Please try again later.';
          }

          console.log('Final error message to user:', errorMessage);
          throw new Error(errorMessage);
        } catch (parseError) {
          // If JSON parsing fails, use generic message
          console.error('Error parsing server response:', parseError);
          console.error('Response status that failed to parse:', response.status);
          throw new Error('Phone registration failed. Please check your connection and try again.');
        }
      }

      const data = await response.json();
      console.log('Phone registration initiated:', data);

    } catch (error) {
      console.error('Phone registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPhone = async (phone: string, token: string) => {
    setIsLoading(true);
    try {
      console.log('Starting phone verification process...');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/verify-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone, token })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Phone verification failed');
      }

      const data = await response.json();
      console.log('Phone verification successful, fetching profile...');

      // Store the access token
      localStorage.setItem('token', data.token);

      // Get user profile from our backend
      const profileResponse = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${data.token}`
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();

        // Check if user is a vendor
        let vendorStatus = null;
        try {
          const vendorResponse = await fetch(`${API_ENDPOINTS.STALLS.VENDORS}/profile`, {
            headers: {
              'Authorization': `Bearer ${data.token}`
            }
          });

          if (vendorResponse.ok) {
            const vendorData = await vendorResponse.json();
            vendorStatus = vendorData.vendor?.status || null;
          }
        } catch (vendorError) {
          // User is not a vendor, which is fine
          console.log('User is not a vendor');
        }

        // Check if user has a driver application
        let driverApplicationStatus = null;
        try {
          const driverResponse = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/my-application`, {
            headers: {
              'Authorization': `Bearer ${data.token}`
            }
          });

          if (driverResponse.ok) {
            const driverData = await driverResponse.json();
            driverApplicationStatus = driverData.application?.status || null;
          }
        } catch (driverError) {
          // User has no driver application, which is fine
          console.log('User has no driver application');
        }

        // Check if driver is assigned to a courier company
        let courierAssignment: any = null;
        if (driverApplicationStatus === 'approved') {
          try {
            const courierResponse = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/driver-assignment`, {
              headers: {
                'Authorization': `Bearer ${data.token}`
              }
            });

            if (courierResponse.ok) {
              const courierData = await courierResponse.json();
              courierAssignment = courierData.assignment || null;
            } else if (courierResponse.status === 404) {
              // Driver is not assigned to any company, which is fine
              console.log('Driver is not assigned to any courier company');
            }
          } catch (courierError) {
            // Driver is not assigned to any company, which is fine
            console.log('Driver is not assigned to any courier company');
          }
        }

        const userRoles = profileData.profile.roles || [profileData.profile.role || 'user'];

        const userData = {
          id: profileData.profile.id,
          email: profileData.profile.email || '',
          name: profileData.profile.fullName || profileData.profile.phone || '',
          role: profileData.profile.role,
          roles: userRoles,
          isVendor: vendorStatus !== null,
          vendorStatus: vendorStatus || undefined,
          driverApplicationStatus: driverApplicationStatus || undefined,
          isDriver: driverApplicationStatus === 'approved',
          isAssignedDriver: courierAssignment !== null,
          courierCompanyId: courierAssignment?.courier_company_id || undefined,
          courierCompanyName: courierAssignment?.courier_company_name || undefined,
          isAdmin: userRoles.includes('admin'),
          isCourier: userRoles.includes('courier')
        };

        console.log('Setting user data:', userData);
        setUser(userData);
      } else {
        throw new Error('Failed to fetch user profile after verification');
      }

      console.log('Phone verification completed successfully');
    } catch (error) {
      console.error('Phone verification error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUserRole = async () => {
    try {
      await checkAuth();
    } catch (error) {
      console.error('Failed to refresh user role:', error);
    }
  };

  const value = {
    user,
    login,
    phoneLogin,
    phoneRegister,
    verifyPhone,
    logout,
    isLoading,
    checkAuth,
    refreshUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
